import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { database, db } from '../firebase';
import { ref, onValue, update, get, set, serverTimestamp } from 'firebase/database';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp as firestoreTimestamp, query, orderBy, collectionGroup, where } from 'firebase/firestore';
import { NavalConstants } from '../constants/NavalConstants';
import '../styles/global.css';

const SPECIALTY_NAME_TO_CODE = {
    'Oficinista': 'OFTA', 'Oficinistas': 'OFTA', 'Infanteria de Marina': 'IM', 'Lavandería': 'LAVD', 'Cocina': 'COCN',
    'Armamento': 'ARMM', 'Navegación': 'NAVG', 'Condestable': 'COND', 'Mecánico': 'MEC', 'Electricista': 'ELEC',
    'Electrónica': 'ELTC', 'Torpedos': 'TORP', 'Comunicaciones': 'COM', 'Radar': 'RAD', 'Sonar': 'SON',
    'Contramedidas': 'CMED', 'Piloto Aviador Naval': 'PAN', 'Observador Aéreo': 'OAN', 'Técnico Aeronáutico': 'TAN',
    'Fusilero': 'FUS', 'Artillero': 'ART', 'Médico': 'MED', 'Enfermero': 'ENF', 'Odontólogo': 'ODO', 'Farmacéutico': 'FAR',
    'Médicos Cirujanos': 'MED_CIR', 'Cirujanos Dentistas': 'CIR_DENT', 'Enfermería': 'ENF', 'Psicología': 'PSIC',
    'Nutrición': 'NUTR', 'Sanidad': 'SAN', 'Servicios Generales de Sanidad': 'SGS', 'Químico': 'QUIM',
    'Dentista': 'DENT', 'Radiologo': 'RADIO', 'Laboratorista': 'LAB', 'Licenciada en Pedagogia': 'PEDAG',
    'Licenciada en Educacion Preescolar': 'EDUC_PRE', 'Licenciada en Educacion Primaria': 'EDUC_PRIM',
    'Alimentación': 'ALIM', 'Servicios Generales': 'SG', 'Servicio de Trabajo Social Naval': 'TSN',
    'Costurero': 'COSTR', 'Sastre': 'SSTR', 'Chofer': 'CHOFR', 'Lavandero': 'LAVDR', 'Jardinero': 'JRDNR',
    'Servicios Generales de alimentacion': 'SGA', 'impresor': 'IMPR', 'Camarero': 'CMRR', 'Cocinero': 'COCN',
    'Panadero': 'PNDR', 'Peluquero': 'PLQR', 'Armamento Aereo': 'ARMM_AER', 'Mecánico de Aviación': 'MEC_AVI',
    'Electrónico de Aviación': 'ELE_AVI', 'Operaciones Aereas': 'OPE_AER', 'Controlador de Vuelo': 'CTRL_VLO',
    'Contramaestres': 'CONTRAM', 'Condestables': 'CONDEST', 'Maquinas': 'MAQ', 'Trabajos Submarinos': 'TRAB_SUB',
    'Proteccion Portuaria': 'PROT_PORT', 'Informatico': 'INF', 'Radio Operador': 'RAD_OP', 'Mecánico Automotriz': 'MEC_AUTO',
    'Hidrografía': 'HIDRO', 'Informática/Computación': 'INF_COMP', 'carpintero ': 'CRPNT', 'Tornero': 'TRNR',
    'Soldador': 'SLDR', 'Electromecanico': 'E_MEC', 'Fontanero': 'FONT', 'Criminalistica': 'CRIM',
    'Licenciado en Derecho': 'DER', 'Mecánicos/Motoristas': 'MEC_MOT', 'Electricistas': 'ELEC',
    'Frigoríficos': 'FRIG', 'Control de Averías': 'CTRL_AV', 'Electrónicos': 'ELECTRON',
    'camillero ': 'CAMILL'
};

// Convierte una pregunta de la Bóveda (Firestore) al formato RTDB de especialidad.
// Soporta { question, options[], correctAnswer, explanation } y { pregunta, respuestas[]/{} }.
const normalizeVaultQuestion = (q, qIdx) => {
    const qId = qIdx + 1;
    const isCorrect = (value) => (
        value === true || value === 1 || String(value) === "1" || String(value).toLowerCase() === "true"
    );

    let respuestas = [];
    const rawRespuestas = q.respuestas || q.options || q.opciones || [];

    if (Array.isArray(rawRespuestas)) {
        respuestas = rawRespuestas.map((opt, optIdx) => {
            const rId = (qId * 10) + optIdx;
            if (typeof opt === 'string') {
                return {
                    id: rId,
                    respuesta: opt,
                    correcta: (Number(optIdx) === Number(q.correctAnswer)) || (opt === q.respuesta) || (opt === q.respuesta_correcta)
                };
            }
            return {
                id: rId,
                respuesta: opt.respuesta || opt.texto || opt.text || 'Sin respuesta',
                correcta: isCorrect(opt.correcta)
            };
        });
    } else if (rawRespuestas && typeof rawRespuestas === 'object') {
        respuestas = Object.values(rawRespuestas).map((opt, optIdx) => {
            const rId = (qId * 10) + optIdx;
            return {
                id: rId,
                respuesta: opt.respuesta || opt.texto || opt.text || 'Sin respuesta',
                correcta: isCorrect(opt.correcta)
            };
        });
    }

    if (!respuestas.some(r => r.correcta) && Array.isArray(rawRespuestas) && q.correctAnswer !== undefined) {
        const ci = parseInt(q.correctAnswer);
        if (!isNaN(ci) && respuestas[ci]) respuestas[ci].correcta = true;
    }

    return {
        id: qId,
        pregunta: q.pregunta || q.question || 'Sin pregunta',
        respuestas,
        explicacion: q.explicacion || q.explanation || ''
    };
};

const AdminContentExplorer = () => {
    const navigate = useNavigate();

    // Metadata options and mappings (Dynamic from RTDB)
    const [hierarchyOptions, setHierarchyOptions] = useState([]);
    const [corpsOptions, setCorpsOptions] = useState([]);
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [specialtiesByCorps, setSpecialtiesByCorps] = useState({});
    const [allSpecialties, setAllSpecialties] = useState([]);
    const [specialtyTemas, setSpecialtyTemas] = useState({});

    // Shared Vault State
    const [sharedQuizzes, setSharedQuizzes] = useState([]);
    const [loadingShared, setLoadingShared] = useState(false);

    // UI Expansion states
    const [expandedSections, setExpandedSections] = useState({
        sharedVault: true,
        userVaults: false,
        specialtyTema: true,
        vaultExplorer: false,
        specialtyExplorer: false
    });

    // Form states - Specialty Tema
    const [specialtyJerarquia, setSpecialtyJerarquia] = useState('');
    const [specialtyCuerpo, setSpecialtyCuerpo] = useState('');
    const [specialtyEspecialidad, setSpecialtyEspecialidad] = useState('');
    const [specialtyTemaId, setSpecialtyTemaId] = useState('');
    const [specialtyTemaNombre, setSpecialtyTemaNombre] = useState('');
    const [specialtyTemaDescripcion, setSpecialtyTemaDescripcion] = useState('');

    // Form states - Quizz (for shared vault handlers)
    const [quizzTitulo, setQuizzTitulo] = useState('');
    const [quizzJson, setQuizzJson] = useState('');

    // Vault Explorer
    const [vaultExplorerItems, setVaultExplorerItems] = useState([]);
    const [loadingVaultExplorer, setLoadingVaultExplorer] = useState(false);
    const [vaultFilterHierarchy, setVaultFilterHierarchy] = useState('');
    const [vaultFilterCorps, setVaultFilterCorps] = useState('');
    const [vaultFilterSpecialty, setVaultFilterSpecialty] = useState('');
    const [vaultFiltersOpen, setVaultFiltersOpen] = useState(true);
    const [vaultCompact, setVaultCompact] = useState(false);

    // Specialty Explorer
    const [specFilterHierarchy, setSpecFilterHierarchy] = useState('');
    const [specFilterCorps, setSpecFilterCorps] = useState('');
    const [specFilterSpecialty, setSpecFilterSpecialty] = useState('');
    const [expandedSpecialtyTemas, setExpandedSpecialtyTemas] = useState(new Set());

    // Asignar ítem de bóveda -> especialidades_temas
    const [assigningItemId, setAssigningItemId] = useState('');
    const [assignDestJerarquia, setAssignDestJerarquia] = useState('');
    const [assignDestCuerpo, setAssignDestCuerpo] = useState('');
    const [assignDestEspecialidad, setAssignDestEspecialidad] = useState('');
    const [assignTemas, setAssignTemas] = useState([]);
    const [assignTemaId, setAssignTemaId] = useState('');
    const [assignLoadingTemas, setAssignLoadingTemas] = useState(false);
    const [assignQuizzId, setAssignQuizzId] = useState('');
    const [assignQuizzTitulo, setAssignQuizzTitulo] = useState('');
    const [assignStatus, setAssignStatus] = useState('');
    const [assignPublishing, setAssignPublishing] = useState(false);
    const [assignNewTema, setAssignNewTema] = useState(false);
    const [assignTemaNombre, setAssignTemaNombre] = useState('');

    useEffect(() => {
        if (!assignDestJerarquia || !assignDestCuerpo || !assignDestEspecialidad) {
            setAssignTemas([]);
            return undefined;
        }
        let cancelled = false;
        setAssignLoadingTemas(true);
        get(ref(database, `especialidades_temas/${assignDestJerarquia}/${assignDestCuerpo}/${assignDestEspecialidad}/temas`))
            .then((snap) => {
                if (cancelled) return;
                if (snap.exists()) {
                    const temas = Object.entries(snap.val()).map(([id, t]) => ({
                        id,
                        nombre: t?.nombre || 'Sin nombre',
                        quizCount: Object.keys(t?.cuestionarios || {}).length
                    }));
                    setAssignTemas(temas);
                } else {
                    setAssignTemas([]);
                }
            })
            .catch(() => { if (!cancelled) setAssignTemas([]); })
            .finally(() => { if (!cancelled) setAssignLoadingTemas(false); });
        return () => { cancelled = true; };
    }, [assignDestJerarquia, assignDestCuerpo, assignDestEspecialidad]);

    // User Vault Inspection & Promotion
    const [userVaultQuizzes, setUserVaultQuizzes] = useState([]);
    const [loadingUserVault, setLoadingUserVault] = useState(false);

    // Metadata helpers (Fallback to constants if metadata not loaded)
    const GRADOS_LIST = hierarchyOptions.length > 0 ? hierarchyOptions.map(h => h.name) : NavalConstants.GRADOS;
    const CUERPOS_LIST = corpsOptions.length > 0 ? corpsOptions.map(c => c.name) : NavalConstants.CUERPOS_Y_SERVICIOS;

    const getSpecialtiesForCuerpo = (cuerpoName) => {
        if (specialtiesByCorps[cuerpoName]) return specialtiesByCorps[cuerpoName];
        return NavalConstants.ESPECIALIDADES[cuerpoName] || [];
    };
    const processMetadata = (data) => {
        const extractCode = (displayName) => {
            const m = displayName.match(/\(([^)]+)\)/);
            return m ? m[1].trim() : displayName;
        };

        // 1. Cuerpos y Servicios
        const corpsList = [];
        if (data.cuerpos_y_servicios) {
            Object.entries(data.cuerpos_y_servicios).forEach(([index, name]) => {
                const idx = parseInt(index);
                let type;
                if (idx >= 0 && idx <= 3) {
                    type = 'cuerpo';
                } else {
                    type = 'servicio';
                }
                const code = extractCode(name);
                corpsList.push({ code, name, type, rtdbIndex: index });
            });
        }
        setCorpsOptions(corpsList);

        // 2. Grados
        if (data.grados) {
            const hierarchies = Object.entries(data.grados).map(([index, value]) => {
                return { code: extractCode(value), name: value, rtdbIndex: index };
            });
            setHierarchyOptions(hierarchies);
        }

        // 3. Especialidades
        const specialtiesMap = {};
        const allSpecsSet = new Set();
        if (data.especialidades) {
            Object.entries(data.especialidades).forEach(([name, specs]) => {
                if (Array.isArray(specs)) {
                    specialtiesMap[name] = specs;
                    specs.forEach(spec => allSpecsSet.add(spec));
                }
            });
        }
        setSpecialtiesByCorps(specialtiesMap);

        const allSpecs = Array.from(allSpecsSet).map(specName => {
            const firestoreCode = SPECIALTY_NAME_TO_CODE[specName] || specName;
            return { code: firestoreCode, name: specName };
        });
        setAllSpecialties(allSpecs);
        setSpecialtyOptions(allSpecs);
    };

    useEffect(() => {
        const unsubs = [
            onValue(ref(database, 'app_metadata'), snap => {
                if (snap.exists()) {
                    const data = snap.val();
                    processMetadata(data);
                }
            })
        ];
        return () => unsubs.forEach(unsub => unsub());
    }, []);

    // Carga SOLO el árbol de especialidades cuando se abre la sección "Explorador de
    // Especialidades Temas". Antes se descargaban los ~42 MB completos al montar el panel.
    const [specialtyTemasLoading, setSpecialtyTemasLoading] = useState(false);
    useEffect(() => {
        if (!expandedSections.specialtyExplorer) return undefined;
        let cancelled = false;
        setSpecialtyTemasLoading(true);
        get(ref(database, 'especialidades_temas'))
            .then((snap) => {
                if (cancelled) return;
                setSpecialtyTemas(snap.exists() ? snap.val() : {});
            })
            .catch(() => { if (!cancelled) setSpecialtyTemas({}); })
            .finally(() => { if (!cancelled) setSpecialtyTemasLoading(false); });
        return () => { cancelled = true; };
    }, [expandedSections.specialtyExplorer]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // --- RENDER HELPERS ---

    const renderHeader = (title, icon, section) => (
        <div className="section-header" onClick={() => toggleSection(section)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '15px' }}>
            <span style={{ fontSize: '1.4rem', marginRight: '10px' }}>{icon}</span>
            <h3 style={{ margin: 0, flex: 1 }}>{title}</h3>
            <span>{expandedSections[section] ? '▲' : '▼'}</span>
        </div>
    );
    // --- SHARED VAULT ACTIONS ---

    const handleFetchSharedQuizzes = async () => {
        console.log("----- FETCHING SHARED QUIZZES START -----");
        setLoadingShared(true);
        try {
            console.log("Querying 'shared_vault' collection...");
            const q = query(collection(db, 'shared_vault'), orderBy('fecha_creacion', 'desc'));
            const querySnapshot = await getDocs(q);
            console.log("Snapshot size:", querySnapshot.size);

            const quizzes = querySnapshot.docs.map(doc => {
                console.log("Doc found:", doc.id, doc.data());
                return {
                    id: doc.id,
                    ...doc.data()
                };
            });
            console.log("Mapped quizzes:", quizzes);
            setSharedQuizzes(quizzes);
        } catch (e) {
            console.error("Error fetching shared vault:", e);
            if (e.code === 'failed-precondition') {
                alert('Error: Falta un índice en Firestore. Revisa la consola para el link de creación.');
            } else {
                alert('Error al cargar la bóveda compartida: ' + e.message);
            }
        } finally {
            setLoadingShared(false);
            console.log("----- FETCHING SHARED QUIZZES END -----");
        }
    };

    const handleUploadToSharedVault = async () => {
        console.log("----- UPLOAD TO SHARED VAULT START -----");
        if (!quizzTitulo || !quizzJson) {
            alert('Debes tener un Título y un JSON válido en el formulario de abajo para subirlo.');
            console.warn("Upload aborted: Missing title or JSON");
            return;
        }

        try {
            console.log("Parsing JSON...");
            let parsedQuestions;
            try {
                parsedQuestions = JSON.parse(quizzJson);
                if (!Array.isArray(parsedQuestions)) throw new Error("El JSON debe ser un array de preguntas");
                console.log("JSON parsed successfully. Questions count:", parsedQuestions.length);
            } catch (e) {
                alert('El JSON no es válido: ' + e.message);
                console.error("JSON parse error:", e);
                return;
            }

            const confirmUpload = window.confirm(`¿Estás seguro de subir "${quizzTitulo}" a la Bóveda Compartida?\n\nSerá visible para TODOS los usuarios de forma anónima.`);
            if (!confirmUpload) {
                console.log("Upload cancelled by user.");
                return;
            }

            setLoadingShared(true);
            const docData = {
                titulo: quizzTitulo,
                preguntas: parsedQuestions,
                fecha_creacion: firestoreTimestamp(),
                tipo: 'general',
                autor: 'anonimo'
            };
            console.log("Preparing to add doc:", docData);

            const docRef = await addDoc(collection(db, 'shared_vault'), docData);
            console.log("Doc added successfully! ID:", docRef.id);

            alert('✅ Cuestionario subido exitosamente a la Bóveda Compartida.');
            handleFetchSharedQuizzes(); // Recargar lista
        } catch (e) {
            console.error("Error uploading to shared vault:", e);
            alert('Error al subir: ' + e.message);
        } finally {
            setLoadingShared(false);
            console.log("----- UPLOAD TO SHARED VAULT END -----");
        }
    };

    const handleDeleteSharedQuiz = async (id) => {
        if (!window.confirm("¿Seguro que quieres eliminar este cuestionario de la bóveda compartida? Esta acción no se puede deshacer.")) return;
        try {
            await deleteDoc(doc(db, 'shared_vault', id));
            setSharedQuizzes(prev => prev.filter(q => q.id !== id));
        } catch (e) {
            alert('Error al eliminar: ' + e.message);
        }
    };
    // --- USER VAULT INSPECTION & PROMOTION ---

    const handleFetchUserVaults = async () => {
        setLoadingUserVault(true);
        try {
            console.log("Fetching all user vaults (collectionGroup 'quizzes' where isVault == true)...");
            const q = query(collectionGroup(db, 'quizzes'), where('isVault', '==', true));
            const querySnapshot = await getDocs(q);

            const quizzes = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ref: doc.ref,
                path: doc.ref.path,
                ...doc.data()
            }));
            console.log("User vault quizzes found:", quizzes.length);
            setUserVaultQuizzes(quizzes);
        } catch (e) {
            console.error("Error fetching user vaults:", e);
            if (e.code === 'failed-precondition') {
                alert('⚠️ Falta un índice en Firestore. Abre la consola (F12) y haz clic en el enlace que aparece en el error para crearlo.');
            } else {
                alert('Error al cargar bóvedas de usuarios: ' + e.message);
            }
        } finally {
            setLoadingUserVault(false);
        }
    };

    const handlePromoteToGlobal = async (userQuiz) => {
        if (!window.confirm(`¿Promover "${userQuiz.name}" a la Bóveda Global?\n\nSe hará público y anónimo.`)) return;

        setLoadingUserVault(true);
        try {
            const questionsRef = collection(userQuiz.ref, 'questions');
            const questionsSnap = await getDocs(questionsRef);

            if (questionsSnap.empty) {
                throw new Error("Este cuestionario no tiene preguntas guardadas.");
            }

            const rebuiltQuestions = questionsSnap.docs.map(doc => {
                const data = doc.data();

                const optionsArray = [];
                let correctIndex = 0;

                const sortedKeys = Object.keys(data.respuestas || {}).sort();

                sortedKeys.forEach((key, index) => {
                    const optObj = data.respuestas[key];
                    optionsArray.push(optObj.respuesta);
                    if (optObj.correcta === 1 || optObj.correcta === true || String(optObj.correcta) === "1" || String(optObj.correcta).toLowerCase() === "true") correctIndex = index;
                });

                return {
                    question: data.pregunta,
                    options: optionsArray,
                    correctAnswer: correctIndex,
                    explanation: data.explicacion || ''
                };
            });

            await addDoc(collection(db, 'shared_vault'), {
                titulo: userQuiz.name,
                preguntas: rebuiltQuestions,
                fecha_creacion: firestoreTimestamp(),
                tipo: 'general_promoted',
                autor: 'anonimo',
                origen: 'promocion_usuario'
            });

            alert(`✅ ¡"${userQuiz.name}" ha sido promovido exitosamente a la Bóveda Global!`);
            handleFetchSharedQuizzes();

        } catch (e) {
            console.error("Error promoting quiz:", e);
            alert('Error al promover: ' + e.message);
        } finally {
            setLoadingUserVault(false);
        }
    };
    // --- SPECIALTY TEMA CREATION ---

    const handleCreateSpecialtyTema = async () => {
        if (!specialtyJerarquia || !specialtyCuerpo || !specialtyEspecialidad || !specialtyTemaId || !specialtyTemaNombre) {
            alert('Campos obligatorios faltantes');
            return;
        }
        const path = `especialidades_temas/${specialtyJerarquia}/${specialtyCuerpo}/${specialtyEspecialidad}/temas/${specialtyTemaId}`;
        try {
            await update(ref(database, path), {
                id: parseInt(specialtyTemaId),
                nombre: specialtyTemaNombre,
                descripcion: specialtyTemaDescripcion,
                jerarquia: specialtyJerarquia,
                cuerpo: specialtyCuerpo,
                especialidad: specialtyEspecialidad
            });
            alert('Tema guardado en especialidades');
            setSpecialtyTemaId(''); setSpecialtyTemaNombre(''); setSpecialtyTemaDescripcion('');
        } catch (e) { alert('Error: ' + e.message); }
    };
    // --- VAULT EXPLORER ---

    const vaultFilteredItems = useMemo(() => {
        return vaultExplorerItems.filter(item => {
            const matchField = (fieldValue, filterValue, options) => {
                if (!filterValue) return true;
                const opt = options.find(o => o.name === filterValue);
                const expectedCode = (opt ? opt.code : filterValue).toUpperCase();
                const actualCode = (fieldValue || '').toUpperCase();
                return actualCode === expectedCode || actualCode === filterValue.toUpperCase();
            };
            if (vaultFilterHierarchy && !matchField(item.hierarchyCode, vaultFilterHierarchy, hierarchyOptions)) return false;
            if (vaultFilterCorps && !matchField(item.corpsCode, vaultFilterCorps, corpsOptions)) return false;
            if (vaultFilterSpecialty && !matchField(item.specialtyCode, vaultFilterSpecialty, specialtyOptions)) return false;
            return true;
        });
    }, [vaultExplorerItems, vaultFilterHierarchy, vaultFilterCorps, vaultFilterSpecialty, hierarchyOptions, corpsOptions, specialtyOptions]);

    const specialtyExplorerData = useMemo(() => {
        const results = [];
        if (!specialtyTemas || Object.keys(specialtyTemas).length === 0) return results;
        Object.entries(specialtyTemas).forEach(([rtdbHierarchy, corpsMap]) => {
            if (specFilterHierarchy && specFilterHierarchy !== rtdbHierarchy) return;
            if (!corpsMap || typeof corpsMap !== 'object') return;
            Object.entries(corpsMap).forEach(([rtdbCorps, specMap]) => {
                if (specFilterCorps && specFilterCorps !== rtdbCorps) return;
                if (!specMap || typeof specMap !== 'object') return;
                Object.entries(specMap).forEach(([rtdbSpecialty, specData]) => {
                    if (rtdbSpecialty === 'habilitado') return;
                    if (specFilterSpecialty && specFilterSpecialty !== rtdbSpecialty) return;
                    if (!specData || typeof specData !== 'object') return;
                    const temasMap = specData.temas || {};
                    const temas = Object.entries(temasMap).map(([temaId, tema]) => {
                        const quizzes = tema?.cuestionarios ? Object.values(tema.cuestionarios) : [];
                        const totalPreguntas = quizzes.reduce((sum, q) => {
                            const p = q?.preguntas;
                            return sum + (Array.isArray(p) ? p.length : Object.keys(p || {}).length);
                        }, 0);
                        return {
                            id: temaId,
                            nombre: tema?.nombre || 'Sin nombre',
                            descripcion: tema?.descripcion || '',
                            quizCount: quizzes.length,
                            questionCount: totalPreguntas,
                            quizzes
                        };
                    });
                    results.push({
                        hierarchy: rtdbHierarchy,
                        corps: rtdbCorps,
                        specialty: rtdbSpecialty,
                        temaCount: temas.length,
                        temas
                    });
                });
            });
        });
        return results;
    }, [specialtyTemas, specFilterHierarchy, specFilterCorps, specFilterSpecialty]);
    const handleFetchVaultExplorer = async () => {
        setLoadingVaultExplorer(true);
        try {
            const results = [];
            const seenIds = new Set();

            const pushDoc = (doc, data) => {
                if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);
                    results.push({
                        id: doc.id,
                        ref: doc.ref,
                        path: doc.ref.path,
                        hierarchyCode: data.hierarchyCode?.toUpperCase() || '',
                        corpsCode: data.corpsCode?.toUpperCase() || '',
                        specialtyCode: data.specialtyCode?.toUpperCase() || '',
                        ...data
                    });
                }
            };

            const hierSnap = await getDocs(query(collection(db, 'shared_vault'), where('isVault', '==', true)));
            hierSnap.docs.forEach(doc => pushDoc(doc, doc.data()));

            const globalSnap = await getDocs(query(collection(db, 'shared_vault'), where('tipo', 'in', ['general', 'general_promoted'])));
            globalSnap.docs.forEach(doc => {
                const d = doc.data();
                const newData = {
                    ...d,
                    hierarchyCode: d.hierarchyCode?.toUpperCase() || '',
                    corpsCode: d.corpsCode?.toUpperCase() || '',
                    specialtyCode: d.specialtyCode?.toUpperCase() || '',
                    _global: true
                };
                pushDoc(doc, newData);
            });

            const subSnap = await getDocs(collectionGroup(db, 'quizzes'));
            subSnap.docs.forEach(doc => {
                const data = doc.data();
                if (data.isVault === true) {
                    pushDoc(doc, data);
                }
            });

            results.sort((a, b) => (b.timestamp?.toDate?.() || 0) - (a.timestamp?.toDate?.() || 0));
            setVaultExplorerItems(results);
        } catch (e) {
            console.error('Error fetching vault explorer:', e);
            if (e.code === 'failed-precondition') {
                alert('⚠️ Falta un índice en Firestore. Abre la consola (F12) y haz clic en el enlace del error para crearlo.');
            } else {
                alert('Error: ' + e.message);
            }
        } finally {
            setLoadingVaultExplorer(false);
        }
    };

    const handleDeleteVaultExplorerItem = async (item) => {
        const label = item.name || item.titulo || 'sin título';
        if (!window.confirm(`¿Eliminar "${label}" permanentemente?\n\nEsta acción no se puede deshacer.\nRuta: ${item.path}`)) return;
        try {
            await deleteDoc(doc(db, item.path));
            setVaultExplorerItems(prev => prev.filter(i => i.id !== item.id));
        } catch (e) {
            alert('Error al eliminar: ' + e.message);
        }
    };

    // --- ASIGNAR ÍTEM DE BÓVEDA A especialidades_temas ---
    const codeToName = (code, options) => {
        if (!code) return '';
        const found = options.find(o => o.code === code || o.name === code);
        return found ? found.name : code;
    };

    const resolveItemQuestions = async (item) => {
        if (Array.isArray(item.preguntas)) return item.preguntas;
        if (item.preguntas && typeof item.preguntas === 'object') {
            return Object.entries(item.preguntas).map(([id, p]) => ({ id, ...p }));
        }
        if (item.path) {
            const qSnap = await getDocs(collection(db, item.path + '/questions'));
            if (!qSnap.empty) return qSnap.docs.map(d => d.data());
        }
        return [];
    };

    const handleStartAssign = (item) => {
        setAssignDestJerarquia(codeToName(item.hierarchyCode, hierarchyOptions));
        setAssignDestCuerpo(codeToName(item.corpsCode, corpsOptions));
        setAssignDestEspecialidad(codeToName(item.specialtyCode, specialtyOptions));
        setAssignTemaId('');
        const count = Array.isArray(item.preguntas) ? item.preguntas.length : (item.questionCount || 0);
        setAssignQuizzId(count > 0 ? `Q${count}` : '');
        setAssignQuizzTitulo(item.titulo || item.name || 'Cuestionario');
        setAssignStatus('');
        setAssigningItemId(item.id);
    };

    const handleAssignPublish = async (item) => {
        if (!assignDestJerarquia || !assignDestCuerpo || !assignDestEspecialidad || !assignTemaId) {
            setAssignStatus('❌ Completa jerarquía, cuerpo, especialidad y tema destino.');
            return;
        }
        if (!assignQuizzId) { setAssignStatus('❌ Indica el ID del cuestionario.'); return; }
        setAssignPublishing(true);
        setAssignStatus('');
        try {
            const raw = await resolveItemQuestions(item);
            if (!raw.length) { setAssignStatus('❌ Este cuestionario no tiene preguntas.'); setAssignPublishing(false); return; }
            const formatted = raw.filter(Boolean).map((q, idx) => normalizeVaultQuestion(q, idx));
            const quizzIdNumber = parseInt((assignQuizzId || '').replace(/[^0-9]/g, '')) || (Date.now() % 100000);
            const path = `especialidades_temas/${assignDestJerarquia}/${assignDestCuerpo}/${assignDestEspecialidad}/temas/${assignTemaId}/cuestionarios/${quizzIdNumber}`;
            if (!window.confirm(`¿Asignar "${assignQuizzTitulo}" (${formatted.length} preguntas)?\n\nDestino:\n${path}`)) {
                setAssignPublishing(false);
                return;
            }
            await set(ref(database, path), {
                id: quizzIdNumber,
                titulo: assignQuizzTitulo,
                preguntas: formatted,
                publicado: serverTimestamp(),
                origen: 'explorador_boveda'
            });
            setAssignStatus(`✅ Asignado (${formatted.length} preguntas) a tema #${assignTemaId}.`);
            setAssigningItemId(null);
        } catch (e) {
            setAssignStatus('❌ Error al asignar: ' + e.message);
        } finally {
            setAssignPublishing(false);
        }
    };

    const handleCreateAssignTema = async () => {
        if (!assignDestJerarquia || !assignDestCuerpo || !assignDestEspecialidad) {
            setAssignStatus('❌ Selecciona jerarquía, cuerpo y especialidad primero.');
            return;
        }
        if (!assignTemaId || !assignTemaNombre) {
            setAssignStatus('❌ Indica el ID y el nombre del nuevo tema.');
            return;
        }
        const temaIdNum = parseInt(assignTemaId);
        if (isNaN(temaIdNum)) { setAssignStatus('❌ El ID del tema debe ser numérico.'); return; }
        setAssignPublishing(true);
        try {
            const path = `especialidades_temas/${assignDestJerarquia}/${assignDestCuerpo}/${assignDestEspecialidad}/temas/${temaIdNum}`;
            await set(ref(database, path), {
                id: temaIdNum,
                nombre: assignTemaNombre,
                descripcion: '',
                jerarquia: assignDestJerarquia,
                cuerpo: assignDestCuerpo,
                especialidad: assignDestEspecialidad
            });
            setAssignStatus(`✅ Tema #${temaIdNum} "${assignTemaNombre}" creado.`);
            setAssignNewTema(false);
            setAssignTemaNombre('');
            setAssignTemaId(String(temaIdNum));
            const snap = await get(ref(database, `especialidades_temas/${assignDestJerarquia}/${assignDestCuerpo}/${assignDestEspecialidad}/temas`));
            if (snap.exists()) {
                setAssignTemas(Object.entries(snap.val()).map(([id, t]) => ({
                    id,
                    nombre: t?.nombre || 'Sin nombre',
                    quizCount: Object.keys(t?.cuestionarios || {}).length
                })));
            }
        } catch (e) {
            setAssignStatus('❌ Error al crear tema: ' + e.message);
        } finally {
            setAssignPublishing(false);
        }
    };
    return (
        <div className="admin-quizz-mgmt" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h1 style={{ color: 'var(--accent)', margin: 0 }}>🧰 Explorador de Contenido y Bóvedas</h1>
                <button onClick={() => window.history.back()} className="btn-secondary">← Volver</button>
            </div>

            <div className="admin-grid" style={{ display: 'grid', gap: '20px' }}>

                {/* SHARED VAULT */}
                <div className="admin-card-expandable">
                    {renderHeader("☁️ Bóveda Compartida (Anónima y Global)", "🌍", "sharedVault")}
                    {expandedSections.sharedVault && (
                        <div className="section-body">
                            <p className="hint">Aquí puedes compartir cuestionarios con <b>toda la comunidad</b> de usuarios de forma anónima desde el formulario de abajo, o gestionar los existentes.</p>

                            <div className="vault-controls" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                <button className="btn-secondary" onClick={handleFetchSharedQuizzes} disabled={loadingShared}>
                                    {loadingShared ? 'Cargando...' : '🔄 Cargar Bóveda'}
                                </button>
                                <button className="btn-save adm-btn adm-btn--secondary"  onClick={handleUploadToSharedVault} disabled={loadingShared}>
                                    ☁️ Subir Cuestionario Actual (Del Formulario 👇)
                                </button>
                            </div>

                            <div className="shared-list" style={{ maxHeight: '300px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '10px' }}>
                                {sharedQuizzes.length === 0 ? (
                                    <p style={{ textAlign: 'center', opacity: 0.5 }}>No hay cuestionarios cargados. Pulsa "Cargar Bóveda".</p>
                                ) : (
                                    sharedQuizzes.map(q => (
                                        <div key={q.id} className="shared-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                            <div>
                                                <strong>{q.titulo}</strong>
                                                <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                                                    {q.preguntas?.length || 0} preguntas • {q.fecha_creacion?.toDate ? new Date(q.fecha_creacion.toDate()).toLocaleDateString() : 'Fecha desconocida'}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <button className="btn-mini-action" onClick={() => {
                                                    setQuizzTitulo(q.titulo);
                                                    setQuizzJson(JSON.stringify(q.preguntas, null, 2));
                                                    alert('Cuestionario copiado al formulario de abajo 👇');
                                                }}>⬇️ Copiar</button>
                                                <button className="btn-mini-reject" onClick={() => handleDeleteSharedQuiz(q.id)}>🗑️</button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {/* --- INSPECCIÓN DE USUARIOS --- */}
                <div className="admin-section" style={{ border: '2px solid #009688' }}>
                    {renderHeader("Inspeccionar Bóvedas de Usuarios", "🕵️", "userVaults")}

                    {expandedSections.userVaults && (
                        <div className="section-content">
                            <p style={{ fontSize: '0.9rem', marginBottom: '15px' }}>
                                Busca cuestionarios guardados por usuarios en sus bóvedas personales y promuévelos a la lista Global.
                            </p>
                            <div className="control-panel">
                                <button onClick={handleFetchUserVaults} className="btn-action" disabled={loadingUserVault}>
                                    {loadingUserVault ? 'Buscando...' : '🔍 Buscar en Todas las Bóvedas'}
                                </button>
                            </div>

                            {userVaultQuizzes.length > 0 ? (
                                <div className="quizz-list">
                                    {userVaultQuizzes.map(q => (
                                        <div key={q.id} className="quizz-item" style={{ borderColor: '#009688' }}>
                                            <div className="quizz-info">
                                                <strong>{q.name || 'Sin nombre'}</strong>
                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                    👤 Autor ID: {q.authorId ? `${q.authorId.substring(0, 6)}...` : 'Desc.'} <br />
                                                    📅 {q.timestamp?.toDate().toLocaleDateString() || 'Fecha desc.'} | ❓ {q.questionCount} pregs.
                                                </div>
                                            </div>
                                            <div className="quizz-actions">
                                                <button
                                                    className="btn-mini-action adm-btn adm-btn--secondary"
                                                    
                                                    onClick={() => handlePromoteToGlobal(q)}
                                                    disabled={loadingUserVault}
                                                >
                                                    🌎 Promover
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="no-data">No se encontraron cuestionarios de usuarios (o no has buscado).</p>
                            )}
                        </div>
                    )}
                </div>
                {/* CREACIÓN DE TEMAS ESPECÍFICOS */}
                <div className="admin-card-expandable">
                    {renderHeader("Crear Tema Específico (Por Especialidad)", "⭐", "specialtyTema")}
                    {expandedSections.specialtyTema && (
                        <div className="section-body form-grid-2">
                            <div className="input-group">
                                <label>Jerarquía</label>
                                <select value={specialtyJerarquia} onChange={e => setSpecialtyJerarquia(e.target.value)}>
                                    <option value="">Seleccionar...</option>
                                    {GRADOS_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Cuerpo / Servicio</label>
                                <select value={specialtyCuerpo} onChange={e => { setSpecialtyCuerpo(e.target.value); setSpecialtyEspecialidad(''); }}>
                                    <option value="">Seleccionar...</option>
                                    {CUERPOS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Especialidad</label>
                                <select value={specialtyEspecialidad} onChange={e => setSpecialtyEspecialidad(e.target.value)}>
                                    <option value="">Seleccionar especialidad...</option>
                                    <option value="TODOS">TODAS</option>
                                    {getSpecialtiesForCuerpo(specialtyCuerpo).map(e => <option key={e} value={e}>{e}</option>)}
                                </select>
                            </div>
                            <div className="form-row-compact">
                                <div className="input-group" style={{ width: '100px' }}>
                                    <label>ID Tema</label>
                                    <input type="number" value={specialtyTemaId} onChange={e => setSpecialtyTemaId(e.target.value)} />
                                </div>
                                <div className="input-group" style={{ flex: 1 }}>
                                    <label>Nombre del Tema</label>
                                    <input type="text" value={specialtyTemaNombre} onChange={e => setSpecialtyTemaNombre(e.target.value)} />
                                </div>
                            </div>
                            <div className="input-group">
                                <label>Descripción</label>
                                <textarea value={specialtyTemaDescripcion} onChange={e => setSpecialtyTemaDescripcion(e.target.value)} rows="2" />
                            </div>
                            <button className="btn-save full-width" onClick={handleCreateSpecialtyTema}>💾 Guardar Tema en Especialidades</button>
                        </div>
                    )}
                </div>
                {/* EXPLORADOR DE BÓVEDA TÉCNICA */}
                <div className="admin-card-expandable">
                    {renderHeader("Explorador de Bóveda Técnica", "🔍", "vaultExplorer")}
                    {expandedSections.vaultExplorer && (
                        <div className="section-body">
                            <p className="hint">Busca y filtra cuestionarios de la <strong>Bóveda Global</strong> (🌐 raíz, sin jerarquía) y la <strong>Bóveda por Jerarquía</strong> (📦 por usuario/especialidad) para identificar duplicados.</p>

                            <div className="vault-controls" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                                <button className="btn-secondary" onClick={handleFetchVaultExplorer} disabled={loadingVaultExplorer}>
                                    {loadingVaultExplorer ? '🔄 Cargando...' : '📥 Cargar Todas las Bóvedas'}
                                </button>
                                <span style={{ fontSize: '0.85rem', color: '#888', alignSelf: 'center' }}>
                                    {vaultExplorerItems.length > 0 ? `${vaultExplorerItems.length} elementos${vaultFilteredItems.length !== vaultExplorerItems.length ? ` (${vaultFilteredItems.length} filtrados)` : ''}` : ''}
                                </span>
                                <button className="adm-btn adm-btn--ghost" onClick={() => setVaultFiltersOpen(o => !o)}>
                                    {vaultFiltersOpen ? '🔼 Ocultar filtros' : '🔽 Mostrar filtros'}
                                </button>
                                <button className="adm-btn adm-btn--ghost" onClick={() => setVaultCompact(c => !c)}>
                                    {vaultCompact ? '🔳 Vista amplia' : '📋 Vista compacta'}
                                </button>
                            </div>

                            {vaultFiltersOpen && (
                                <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                                    <div className="input-group">
                                        <label>Filtrar por Jerarquía</label>
                                        <select value={vaultFilterHierarchy} onChange={e => setVaultFilterHierarchy(e.target.value)}>
                                            <option value="">Todas</option>
                                            {GRADOS_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                    </div>
                                    <div className="input-group">
                                        <label>Filtrar por Cuerpo / Servicio</label>
                                        <select value={vaultFilterCorps} onChange={e => { setVaultFilterCorps(e.target.value); setVaultFilterSpecialty(''); }}>
                                            <option value="">Todos</option>
                                            {CUERPOS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div className="input-group">
                                        <label>Filtrar por Especialidad</label>
                                        <select value={vaultFilterSpecialty} onChange={e => setVaultFilterSpecialty(e.target.value)}>
                                            <option value="">Todas</option>
                                            {getSpecialtiesForCuerpo(vaultFilterCorps).map(e => <option key={e} value={e}>{e}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}
                            <div className="shared-list" style={{ maxHeight: '68vh', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '10px' }}>
                                {vaultExplorerItems.length === 0 ? (
                                    <p style={{ textAlign: 'center', opacity: 0.5 }}>No hay datos. Pulsa "Cargar Todas las Bóvedas".</p>
                                ) : vaultFilteredItems.length === 0 ? (
                                    <p style={{ textAlign: 'center', opacity: 0.5 }}>No hay elementos con esos filtros.</p>
                                ) : (
                                    vaultFilteredItems.map((item, idx) => {
                                        const stdId = item.standardizedId || '';
                                        const name = item.name || item.titulo || 'Sin nombre';
                                        const h = item.hierarchyCode || '-';
                                        const c = item.corpsCode || '-';
                                        const s = item.specialtyCode || '-';
                                        const qty = item.questionCount || item.preguntas?.length || 0;
                                        const dateStr = item.timestamp?.toDate?.()?.toLocaleDateString() || item.fecha_creacion?.toDate?.()?.toLocaleDateString() || '';
                                        return (
                                            <div key={item.id || idx} className="shared-item" style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap',
                                                padding: vaultCompact ? '5px 8px' : '10px', borderBottom: '1px solid rgba(255,255,255,0.05)',
                                                background: idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'
                                            }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <strong style={{ fontSize: '0.85rem' }}>{name}</strong>
                                                    <div style={{ fontSize: '0.75rem', opacity: 0.7, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                        <span style={{
                                                            fontSize: '0.65rem', padding: '1px 5px', borderRadius: '3px',
                                                            background: item._global ? 'rgba(255,200,0,0.15)' : 'rgba(0,200,255,0.15)',
                                                            color: item._global ? '#ffc107' : '#0dcaf0'
                                                        }}>
                                                            {item._global ? '🌐 Global' : '📦 Por Jerarquía'}
                                                        </span>
                                                        {stdId && <code style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.05)', padding: '1px 4px', borderRadius: '3px' }}>{stdId}</code>}
                                                        <span>🎖️ {h}</span>
                                                        <span>📂 {c}</span>
                                                        <span>🏷️ {s}</span>
                                                    </div>
                                                    <div style={{ fontSize: '0.7rem', opacity: 0.5, marginTop: '2px' }}>
                                                        {qty} preguntas {dateStr ? `• ${dateStr}` : ''} • <code style={{ fontSize: '0.65rem' }}>{item.path}</code>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '5px', flexShrink: 0, marginLeft: '10px' }}>
                                                    <button className="adm-btn adm-btn--primary" onClick={() => handleStartAssign(item)}>📤 Asignar</button>
                                                    <button className="btn-mini-reject" onClick={() => handleDeleteVaultExplorerItem(item)}>🗑️</button>
                                                </div>
                                                {assigningItemId === item.id && (
                                                    <div style={{ width: '100%', marginTop: '10px', padding: '12px', background: 'rgba(59,130,246,0.08)', border: '1px solid #3b82f6', borderRadius: '8px' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                                            <div className="input-group">
                                                                <label>Jerarquía</label>
                                                                <select value={assignDestJerarquia} onChange={e => { setAssignDestJerarquia(e.target.value); setAssignTemaId(''); }}>
                                                                    <option value="">Seleccionar...</option>
                                                                    {GRADOS_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                                                                </select>
                                                            </div>
                                                            <div className="input-group">
                                                                <label>Cuerpo / Servicio</label>
                                                                <select value={assignDestCuerpo} onChange={e => { setAssignDestCuerpo(e.target.value); setAssignDestEspecialidad(''); setAssignTemaId(''); }}>
                                                                    <option value="">Seleccionar...</option>
                                                                    {CUERPOS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                                                </select>
                                                            </div>
                                                            <div className="input-group">
                                                                <label>Especialidad</label>
                                                                <select value={assignDestEspecialidad} onChange={e => { setAssignDestEspecialidad(e.target.value); setAssignTemaId(''); }}>
                                                                    <option value="">Seleccionar...</option>
                                                                    {getSpecialtiesForCuerpo(assignDestCuerpo).map(e => <option key={e} value={e}>{e}</option>)}
                                                                </select>
                                                            </div>
                                                            <div className="input-group">
                                                                <label>Tema destino</label>
                                                                {assignNewTema ? (
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                                            <input type="number" placeholder="ID tema" value={assignTemaId} onChange={e => setAssignTemaId(e.target.value)} style={{ width: '90px' }} />
                                                                            <input type="text" placeholder="Nombre del tema" value={assignTemaNombre} onChange={e => setAssignTemaNombre(e.target.value)} style={{ flex: 1 }} />
                                                                        </div>
                                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                                            <button className="adm-btn adm-btn--primary" onClick={handleCreateAssignTema} disabled={assignPublishing}>✅ Crear tema</button>
                                                                            <button className="adm-btn adm-btn--secondary" onClick={() => { setAssignNewTema(false); setAssignTemaNombre(''); }}>Cancelar</button>
                                                                        </div>
                                                                    </div>
                                                                ) : assignLoadingTemas ? (
                                                                    <select disabled><option>⏳ Cargando temas...</option></select>
                                                                ) : (
                                                                    <>
                                                                        <select value={assignTemaId} onChange={e => setAssignTemaId(e.target.value)} disabled={!assignDestJerarquia || !assignDestCuerpo || !assignDestEspecialidad}>
                                                                            <option value="">Seleccionar tema...</option>
                                                                            {assignTemas.map(t => <option key={t.id} value={t.id}>#{t.id} {t.nombre} ({t.quizCount} quiz)</option>)}
                                                                        </select>
                                                                        {assignDestJerarquia && assignDestCuerpo && assignDestEspecialidad && (
                                                                            <button className="adm-btn adm-btn--ghost" style={{ marginTop: '6px' }} onClick={() => { setAssignTemaId(''); setAssignTemaNombre(''); setAssignNewTema(true); }}>
                                                                                ➕ Crear nuevo tema
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                                            <div className="input-group">
                                                                <label>ID Cuestionario</label>
                                                                <input type="text" placeholder="Ej: Q1" value={assignQuizzId} onChange={e => setAssignQuizzId(e.target.value)} />
                                                            </div>
                                                            <div className="input-group">
                                                                <label>Título</label>
                                                                <input type="text" value={assignQuizzTitulo} onChange={e => setAssignQuizzTitulo(e.target.value)} />
                                                            </div>
                                                        </div>
                                                        {assignStatus && (
                                                            <div style={{ fontSize: '0.8rem', marginBottom: '8px', color: assignStatus.startsWith('✅') ? '#10b981' : '#ef4444' }}>{assignStatus}</div>
                                                        )}
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button className="adm-btn adm-btn--primary" onClick={() => handleAssignPublish(item)} disabled={assignPublishing}>
                                                                {assignPublishing ? '⏳ Asignando...' : '🚀 Asignar a Especialidad'}
                                                            </button>
                                                            <button className="adm-btn adm-btn--secondary" onClick={() => setAssigningItemId(null)}>Cancelar</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {/* EXPLORADOR DE ESPECIALIDADES TEMAS */}
                <div className="admin-card-expandable">
                    {renderHeader("Explorador de Especialidades Temas", "📚", "specialtyExplorer")}
                    {expandedSections.specialtyExplorer && (
                        <div className="section-body">
                            <p className="hint">Explora los temas y cuestionarios organizados por jerarquía, cuerpo/servicio y especialidad en el RTDB.</p>

                            <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                                <div className="input-group">
                                    <label>Filtrar por Jerarquía</label>
                                    <select value={specFilterHierarchy} onChange={e => { setSpecFilterHierarchy(e.target.value); setSpecFilterCorps(''); setSpecFilterSpecialty(''); }}>
                                        <option value="">Todas</option>
                                        {GRADOS_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                </div>
                                <div className="input-group">
                                    <label>Filtrar por Cuerpo / Servicio</label>
                                    <select value={specFilterCorps} onChange={e => { setSpecFilterCorps(e.target.value); setSpecFilterSpecialty(''); }}>
                                        <option value="">Todos</option>
                                        {CUERPOS_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="input-group">
                                    <label>Filtrar por Especialidad</label>
                                    <select value={specFilterSpecialty} onChange={e => setSpecFilterSpecialty(e.target.value)}>
                                        <option value="">Todas</option>
                                        {getSpecialtiesForCuerpo(specFilterCorps).map(e => <option key={e} value={e}>{e}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="shared-list" style={{ maxHeight: '500px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '10px' }}>
                                {specialtyTemasLoading ? (
                                    <p style={{ textAlign: 'center', opacity: 0.7 }}>⏳ Cargando árbol de especialidades...</p>
                                ) : specialtyExplorerData.length === 0 ? (
                                    <p style={{ textAlign: 'center', opacity: 0.5 }}>No hay datos. Selecciona filtros para explorar.</p>
                                ) : (
                                    specialtyExplorerData.map((entry, idx) => (
                                        <div key={`${entry.hierarchy}--${entry.corps}--${entry.specialty}`} className="shared-item" style={{
                                            padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.08)',
                                            background: idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'
                                        }}>
                                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '4px' }}>
                                                🎖️ {entry.hierarchy} &gt; 📂 {entry.corps} &gt; 🏷️ {entry.specialty}
                                                <span style={{ fontSize: '0.75rem', opacity: 0.6, marginLeft: '8px' }}>({entry.temaCount} temas)</span>
                                            </div>
                                            {entry.temas.map(tema => {
                                                const isExpanded = expandedSpecialtyTemas.has(`${entry.hierarchy}///${entry.corps}///${entry.specialty}`);
                                                return (
                                                    <div key={tema.id} style={{ marginLeft: '15px', marginTop: '4px', fontSize: '0.8rem' }}>
                                                        <div
                                                            onClick={() => {
                                                                const key = `${entry.hierarchy}///${entry.corps}///${entry.specialty}`;
                                                                setExpandedSpecialtyTemas(prev => {
                                                                    const next = new Set(prev);
                                                                    next.has(key) ? next.delete(key) : next.add(key);
                                                                    return next;
                                                                });
                                                            }}
                                                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 6px', borderRadius: '4px', background: 'rgba(255,255,255,0.03)' }}
                                                        >
                                                            <span>{isExpanded ? '▼' : '▶'}</span>
                                                            <strong>{tema.nombre}</strong>
                                                            <span style={{ opacity: 0.5, fontSize: '0.7rem' }}>
                                                                📝 {tema.questionCount} preg • 📚 {tema.quizCount} quiz
                                                            </span>
                                                            {tema.descripcion && <span style={{ opacity: 0.4, fontSize: '0.7rem' }}>— {tema.descripcion}</span>}
                                                        </div>
                                                        {isExpanded && tema.quizzes.length > 0 && (
                                                            <div style={{ marginLeft: '25px', marginTop: '3px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                {tema.quizzes.map((q, qi) => {
                                                                    const pCount = Array.isArray(q?.preguntas) ? q.preguntas.length : Object.keys(q?.preguntas || {}).length;
                                                                    return (
                                                                        <div key={qi} style={{
                                                                            fontSize: '0.7rem', padding: '2px 8px', borderRadius: '3px',
                                                                            background: 'rgba(255,255,255,0.03)', display: 'flex', gap: '6px'
                                                                        }}>
                                                                            <span style={{ opacity: 0.6 }}>#{q.id}</span>
                                                                            <span>{q.titulo || 'Sin título'}</span>
                                                                            <span style={{ opacity: 0.5 }}>{pCount} preguntas</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .admin-quizz-mgmt { color: white; }
                .admin-card-expandable { background: var(--bg-secondary); border-radius: 15px; overflow: hidden; border: 1px solid rgba(255,255,255,0.05); margin-bottom: 20px; transition: all 0.3s ease; }
                .admin-card-expandable:hover { border-color: var(--accent); transform: translateY(-2px); box-shadow: 0 5px 15px rgba(0,0,0,0.3); }
                .section-header { background: rgba(255,255,255,0.02); border-bottom: 1px solid rgba(255,255,255,0.05); }
                .section-body { padding: 20px; animation: slideDown 0.3s ease-out; }
                
                .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
                .form-row-compact { display: flex; gap: 15px; grid-column: span 2; }
                .input-group { display: flex; flex-direction: column; gap: 5px; }
                .input-group label { font-size: 0.8rem; font-weight: bold; color: var(--accent); }
                .input-group input, .input-group select, .input-group textarea { padding: 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.2); color: white; }
                
                .chip { padding: 5px 12px; background: rgba(255,255,255,0.05); border-radius: 20px; font-size: 0.75rem; cursor: pointer; border: 1px solid rgba(255,255,255,0.1); transition: 0.2s; }
                .chip.active { background: var(--accent); color: white; border-color: var(--accent); font-weight: bold; }
                
                .theme-selector-box { border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; background: rgba(0,0,0,0.1); overflow: hidden; }
                .search-input { width: 100%; border: none; padding: 15px; background: rgba(255,255,255,0.03); color: white; border-bottom: 1px solid rgba(255,255,255,0.05); }
                .theme-list-scroll { max-height: 250px; overflow-y: auto; padding: 10px; }
                .theme-item-check { display: flex; align-items: center; gap: 10px; padding: 10px; border-radius: 8px; cursor: pointer; transition: 0.2s; }
                .theme-item-check:hover { background: rgba(255,255,255,0.05); }
                
                .proposal-item-card { display: flex; justify-content: space-between; align-items: center; background: rgba(0,0,0,0.2); padding: 15px; border-radius: 10px; margin-bottom: 10px; border-left: 4px solid var(--accent); }
                .prop-info { display: flex; flex-direction: column; gap: 2px; }
                .prop-info strong { color: var(--accent); }
                .prop-info span { font-size: 0.8rem; opacity: 0.6; }
                .prop-actions { display: flex; gap: 10px; }
                .btn-mini-approve { background: var(--success-color); color: white; border: none; border-radius: 4px; padding: 5px 12px; cursor: pointer; }
                .btn-mini-action { background: var(--accent); color: white; border: none; border-radius: 4px; padding: 5px 12px; cursor: pointer; }
                .btn-mini-reject { background: #f44336; color: white; border: none; border-radius: 4px; padding: 5px 10px; cursor: pointer; }
                .full-width { width: 100%; }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @media (max-width: 768px) {
                    .form-grid-2 { grid-template-columns: 1fr; }
                    .form-row-compact { flex-direction: column; grid-column: span 1; }
                }
            `}</style>
        </div>
    );
};

export default AdminContentExplorer;
